import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { keys, isEmpty, get, pickBy } from 'lodash-es';
import { Container, Row, Col } from 'reactstrap';
/* Models */
import { InventoryEntities, InventoryModel } from 'client/data/models/inventory';
import { VehicleEntities } from 'client/data/models/vehicle';
import {
  ConsumerReviewsEntities,
  ConsumerReviewsModel,
  ConsumerReviewsPaths,
} from 'client/data/models/consumer-reviews';
/* Constants */
import { NAVIGATION_IDS, NAVIGATION_TITLES } from 'client/site-modules/shared/constants/inventory/vdp-navigation';
import { GRID } from 'client/site-modules/inventory/constants/grid';
/* Utils */
import { hasDealerAddressInfo } from 'site-modules/shared/utils/inventory-utils/has-dealer-address-info';
import { showFeaturesTab } from 'site-modules/shared/utils/inventory/show-features-tab';
import { isPartnerDealer, isPrivateParty } from 'site-modules/shared/utils/dealer-details-utils';
import { ConsumerReviewsParams } from 'site-modules/shared/utils/consumer-reviews/params';
import { bindToPath, connectToModel } from 'client/data/luckdragon/redux/react-binding';
import { isUsed } from 'site-modules/shared/utils/inventory-utils/is-used';
import { getVinWarrantyPath } from 'site-modules/shared/utils/inventory/vdp-utils';
import { isWarrantyApplicable, isWarrantyDefined } from 'site-modules/shared/utils/inventory/warranty';
/* Components */
import { AnimatedSticky } from 'site-modules/shared/components/animated-sticky/animated-sticky';
import { VdpActions } from 'client/site-modules/inventory/components/vdp-actions/vdp-actions';
import { AnchorNavContent } from 'site-modules/shared/components/anchor-nav-sticky/anchor-nav-content';

import './vdp-navigation.scss';

export function VdpNavigationUI({
  vinData = {},
  isMobile = false,
  disableSticky = true,
  showPriceSection = false,
  isCalculatorEnabled = false,
  consumerReviewsPrevYear = null,
  consumerReviews = null,
  params = null,
  warranty = [],
  showEVModule = false,
  showPhevModule = false,
  showSellerModule = false,
  showVehicleConditionModule = false,
}) {
  const { type } = vinData;
  const isPartnerListing = isPartnerDealer(vinData);
  const isPrivatePartyListing = isPrivateParty(vinData);
  const isUsedVehicle = isUsed(type);
  const reviewsObj = consumerReviewsPrevYear || consumerReviews;

  const menuKeys = pickBy(
    {
      [NAVIGATION_IDS.SUMMARY]: true,
      [NAVIGATION_IDS.HISTORY]: isUsedVehicle && !isPartnerListing,
      [NAVIGATION_IDS.EV_INSIGHTS]: showEVModule,
      [NAVIGATION_IDS.PHEV_INSIGHTS]: showPhevModule,
      [NAVIGATION_IDS.CONDITION]: showVehicleConditionModule,
      [NAVIGATION_IDS.FEATURES]: showFeaturesTab(vinData),
      [NAVIGATION_IDS.PRICE]: showPriceSection,
      [NAVIGATION_IDS.CALCULATOR]: isCalculatorEnabled,
      [NAVIGATION_IDS.WARRANTY]:
        isWarrantyApplicable(vinData) && isWarrantyDefined(vinData, warranty) && !isPrivatePartyListing,
      [NAVIGATION_IDS.DEALER]: hasDealerAddressInfo(vinData) && !isPrivatePartyListing,
      [NAVIGATION_IDS.SELLER]: showSellerModule,
      [NAVIGATION_IDS.REVIEWS]: !isEmpty(reviewsObj) && !!params,
    },
    value => !!value
  );
  const menuItems = keys(menuKeys);
  const navList = menuItems.map(hash => ({ hash, name: NAVIGATION_TITLES[hash] }));
  const navElement = (
    <Row className={classNames('list-row bg-white', { 'with-bottom-border': isMobile })}>
      <Col xs={GRID.TWO_COL.XS.FULL_WIDTH} className="vdp d-flex justify-content-between px-0_5 injected-container">
        <AnchorNavContent
          navList={navList}
          ariaLabel="sticky skip to section"
          className="me-0_5 me-lg-0"
          listClassName="vdp-navigation-list d-flex flex-nowrap m-0 me-0 me-md-1 p-0"
          itemClassName="pe-1 pe-xl-1_5"
          linkClassName="vdp-navigation-link d-inline-block pb-0_75 pt-1"
          activeClass="link-active text-primary-darker fw-bold"
          isMobile
          addTitleAttr
          defaultActiveHash={NAVIGATION_IDS.SUMMARY}
        />
        {!isMobile && (
          <div className="vdp-actions-container d-flex justify-content-center align-items-center">
            <VdpActions vinData={vinData} spinnerSize={16} trackingParent="vdp-navigation" />
          </div>
        )}
      </Col>
    </Row>
  );

  return (
    <div className="vdp-navigation pt-md-0_5" data-tracking-parent="vdp-navigation">
      <AnimatedSticky
        disableSticky={disableSticky}
        rootMargin="0px 0px 770px 0px"
        stickyClassName={isMobile ? 'px-0_5' : 'px-0'}
        stickyFragment={
          <div
            className={classNames(
              'vdp-navigation-container vdp-navigation-container-sticky-fragment pos-r bg-white heading-6 fw-normal',
              {
                'with-bottom-border': !isMobile,
                'px-0_5': isMobile,
              }
            )}
            data-vdp-navigation
          >
            <Container className="px-0">{navElement}</Container>
          </div>
        }
      >
        <div
          className={classNames('vdp-navigation-container pos-r bg-white heading-6 fw-normal', {
            'with-bottom-border': !isMobile,
          })}
          data-vdp-navigation
        >
          <Container className="px-0">{navElement}</Container>
        </div>
      </AnimatedSticky>
    </div>
  );
}

VdpNavigationUI.propTypes = {
  params: ConsumerReviewsParams,
  consumerReviews: ConsumerReviewsEntities.ConsumerReviewsContent,
  consumerReviewsPrevYear: ConsumerReviewsEntities.ReviewContent,
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  disableSticky: PropTypes.bool,
  showPriceSection: PropTypes.bool,
  showEVModule: PropTypes.bool,
  showPhevModule: PropTypes.bool,
  isCalculatorEnabled: PropTypes.bool,
  warranty: InventoryEntities.Warranty,
  showSellerModule: PropTypes.bool,
  showVehicleConditionModule: PropTypes.bool,
};

/**
 * Double wrapped component for consumer review data resolving.
 */
const VdpNavigationWrapper = connectToModel(VdpNavigationUI, {
  consumerReviewsPrevYear: bindToPath(({ ratingsCount = null, vehicle = null }) => {
    /**
     * In case if the vehicle do not have consumer reviews for current year trying to get reviews for previous year
     */
    if ((!ratingsCount || !ratingsCount.totalReviews) && !isEmpty(vehicle)) {
      const { make, model, year, submodels } = vehicle;
      const prevYear = year - 1;

      return ConsumerReviewsPaths.buildConsumerReviewsPathFromSubmodel({ make, model, year: prevYear, submodels });
    }
    return '';
  }, ConsumerReviewsModel),
});

VdpNavigationWrapper.propTypes = {
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  params: ConsumerReviewsParams,
  consumerReviews: ConsumerReviewsEntities.ConsumerReviewsContent,
  ratingsCount: ConsumerReviewsEntities.ConsumerReviewsRatingsCount,
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  disableSticky: PropTypes.bool,
  showPriceSection: PropTypes.bool,
  showEVModule: PropTypes.bool,
  showPhevModule: PropTypes.bool,
  isCalculatorEnabled: PropTypes.bool,
  warranty: InventoryEntities.Warranty,
  showSellerModule: PropTypes.bool,
  showVehicleConditionModule: PropTypes.bool,
};

export const VdpNavigation = connectToModel(VdpNavigationWrapper, {
  consumerReviews: bindToPath(ConsumerReviewsPaths.getConsumerReviewsPath(), ConsumerReviewsModel),
  ratingsCount: bindToPath(({ params = null, vehicle = null }) => {
    if (get(params, 'styleId')) {
      return ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathByStyleId(params.styleId);
    }

    return ConsumerReviewsPaths.buildConsumerReviewsRatingsCountPathFromVehicle(vehicle);
  }, ConsumerReviewsModel),
  warranty: bindToPath(({ vinData = {} }) => getVinWarrantyPath(vinData), InventoryModel),
});

VdpNavigation.propTypes = {
  vinData: InventoryEntities.InventoryVin,
  isMobile: PropTypes.bool,
  disableSticky: PropTypes.bool,
  showPriceSection: PropTypes.bool,
  showEVModule: PropTypes.bool,
  showPhevModule: PropTypes.bool,
  isCalculatorEnabled: PropTypes.bool,
  vehicle: VehicleEntities.MakeModelSubmodelYear,
  params: ConsumerReviewsParams,
  showSellerModule: PropTypes.bool,
  showVehicleConditionModule: PropTypes.bool,
};
